import { useCallback, useEffect, useRef, useState } from 'react';

interface UseImageZoomProps {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
}

export const useImageZoom = ({
  minZoom = 1,
  maxZoom = 5,
  zoomStep = 0.25
}: UseImageZoomProps = {}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Zoom In
  const zoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + zoomStep, maxZoom));
  }, [maxZoom, zoomStep]);

  // Zoom Out
  const zoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - zoomStep, minZoom);
      if (newZoom === minZoom) {
        setPan({ x: 0, y: 0 }); // Reset pan quando volta ao zoom 1
      }
      return newZoom;
    });
  }, [minZoom, zoomStep]);

  // Reset Zoom
  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Zoom para um valor específico
  const setZoomLevel = useCallback(
    (level: number) => {
      const clampedZoom = Math.max(minZoom, Math.min(level, maxZoom));
      setZoom(clampedZoom);
      if (clampedZoom === minZoom) {
        setPan({ x: 0, y: 0 });
      }
    },
    [minZoom, maxZoom]
  );

  // Handler do wheel (Ctrl + Scroll)
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Calcular posição relativa do mouse (0-1)
        const relativeX = mouseX / rect.width;
        const relativeY = mouseY / rect.height;

        // Calcular novo zoom
        const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
        const newZoom = Math.max(minZoom, Math.min(zoom + delta, maxZoom));

        if (newZoom !== zoom) {
          // Ajustar pan para fazer zoom na posição do mouse
          const zoomDiff = newZoom - zoom;
          const panAdjustX = (relativeX - 0.5) * rect.width * zoomDiff;
          const panAdjustY = (relativeY - 0.5) * rect.height * zoomDiff;

          setPan(prev => ({
            x: prev.x - panAdjustX,
            y: prev.y - panAdjustY
          }));

          setZoom(newZoom);
        }

        if (newZoom === minZoom) {
          setPan({ x: 0, y: 0 });
        }
      }
    },
    [zoom, minZoom, maxZoom, zoomStep]
  );

  // Drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
    },
    [zoom, pan]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoom > 1) {
        setPan({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Adicionar/remover listener do wheel
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  return {
    zoom,
    pan,
    isDragging,
    containerRef,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoomLevel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  };
};
